import type { ComponentType } from 'react'

/**
 * Контракты ядра.
 *
 * Здесь описано, что такое «игра» с точки зрения движка.
 * Ядро НИКОГДА не импортирует ничего из games/ - только этот контракт.
 * Добавить новую игру = реализовать GameModule и положить в registry.
 *
 * Строки интерфейса (titleKey) - обычные string, а не типы i18n:
 * core не должен зависеть от platform/. Типобезопасность ключей
 * обеспечивается на стороне игры, при объявлении модуля.
 */

export type Discipline = 'memory' | 'attention' | 'perception'

/** Все дисциплины в порядке показа. Единственный источник правды. */
export const DISCIPLINES: readonly Discipline[] = ['memory', 'attention', 'perception']

/**
 * Как измеряется результат в этой игре.
 *   span           - объём (память): сколько элементов удержал
 *   speed-accuracy - скорость + точность (внимание): сводятся через BIS
 * Подробнее: docs/form-index-math.md, раздел 2.
 */
export type Metric = 'span' | 'speed-accuracy'

/** Детерминированный генератор случайных чисел. Одинаковый seed = одинаковый раунд. */
export interface Rng {
  /** Число в [0, 1) */
  next(): number
  /** Целое в [0, max) */
  int(max: number): number
  /** Случайный элемент массива. Бросает на пустом массиве. */
  pick<T>(items: readonly T[]): T
  /** Новый перемешанный массив, исходный не меняется. */
  shuffle<T>(items: readonly T[]): T[]
}

/** Итог одного раунда - то, что игра возвращает движку. */
export interface RoundOutcome {
  /** Раунд засчитан полностью */
  correct: boolean
  /** Доля верных элементов, 0..1. Для бинарного ответа - 0 или 1. */
  accuracy: number
  /** Время ответа в миллисекундах */
  rtMs: number
}

/** Пропсы, которые движок передаёт в View игры. */
export interface GameViewProps<TRound, TAnswer> {
  round: TRound
  level: number
  /** Вызвать, когда игрок ответил. Движок сам посчитает и перейдёт дальше. */
  onAnswer: (answer: TAnswer) => void
}

/**
 * Манифест игры. Единственное, что движок знает про упражнение.
 *
 * generateRound и evaluate - ЧИСТЫЕ функции: без побочных эффектов,
 * без обращения к времени и Math.random напрямую (только через rng).
 * Именно это делает их тестируемыми и воспроизводимыми.
 *
 * КОНТРАКТ ЗАМОРОЖЕН после фазы 2.3 (ретро на Memory Matrix).
 * Правки возможны, но теперь затрагивают все игры сразу -
 * сначала обсуждаем, потом меняем. См. docs/phases/06-memory-matrix.md.
 */
export interface GameModule<TRound = unknown, TAnswer = unknown> {
  readonly id: string
  readonly titleKey: string
  readonly hintKey: string
  readonly discipline: Discipline
  readonly metric: Metric

  readonly difficulty: {
    readonly min: number
    readonly max: number
    readonly initial: number
  }

  /** Сколько раундов в одном заходе */
  readonly roundsPerSession: number

  /**
   * Эталонное время ответа для этой игры, мс.
   *
   * Нужно, чтобы вычислить личный темп игрока: его обычное время,
   * делённое на эталон. Шкалы у игр разные (вспомнить паттерн - это
   * секунды, нажать цвет - доли секунды), поэтому единой константы
   * быть не может.
   */
  readonly referenceRtMs: number

  /**
   * @param pace личный темп: 1 - как эталон, больше 1 - игрок медленнее.
   *   Игра может растянуть тайминги под него, а может проигнорировать,
   *   если время в ней не ограничено.
   */
  generateRound(level: number, rng: Rng, pace?: number): TRound
  evaluate(round: TRound, answer: TAnswer): RoundOutcome

  readonly View: ComponentType<GameViewProps<TRound, TAnswer>>
}

/** Модуль с любыми параметрами - для хранения в реестре. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type AnyGameModule = GameModule<any, any>

/** Агрегат одного захода - то, что уходит в хранилище. */
export interface SessionStats {
  /** Доля правильных раундов, 0..1 */
  readonly pc: number
  /** Среднее время правильных ответов, мс */
  readonly rtMs: number
  /** Достигнутый объём (для span-игр) */
  readonly span: number
  /** Уровень, на котором закончили */
  readonly level: number
  /** Метка времени, мс */
  readonly at: number
}

/** Границы личного темпа. Уже - игра станет неиграбельной, шире - потеряет смысл. */
export const MIN_PACE = 0.75
export const MAX_PACE = 1.8
/** Темп по умолчанию, пока истории нет. Чуть щедрее эталона - первый заход не должен гнать. */
export const DEFAULT_PACE = 1.15

/**
 * Личный темп по истории игрока.
 * Берём медиану, а не среднее: один заход с зависанием не должен
 * растягивать тайминги навсегда.
 */
export function paceFrom(history: readonly SessionStats[], referenceRtMs: number): number {
  const times = history
    .map((h) => h.rtMs)
    .filter((t) => t > 0)
    .sort((a, b) => a - b)
  if (times.length === 0 || referenceRtMs <= 0) return DEFAULT_PACE
  const mid = Math.floor(times.length / 2)
  const median =
    times.length % 2 === 1
      ? (times[mid] as number)
      : ((times[mid - 1] as number) + (times[mid] as number)) / 2
  return Math.min(Math.max(median / referenceRtMs, MIN_PACE), MAX_PACE)
}

/** Сколько первых раундов идут в щадящем темпе. */
export const WARMUP_ROUNDS = 2
/** Насколько мягче разминка относительно обычного темпа игрока. */
export const WARMUP_FACTOR = 1.3
/**
 * Вес сохранённого темпа при смешивании с наблюдаемым.
 * Три - значит первые наблюдения влияют заметно, но не мгновенно.
 */
export const STORED_PACE_WEIGHT = 3

/**
 * Темп внутри захода.
 *
 * Первые раунды идут мягче обычного: человек ещё не включился, и
 * жёсткий тайминг на старте читается как «игра слишком быстрая»,
 * даже если дальше он справляется. Дальше темп подстраивается под
 * то, что игрок показывает ПРЯМО СЕЙЧАС, смешиваясь с сохранённым.
 *
 * Расчёт ведётся отдельно для каждой игры: скорость в Stroop ничего
 * не говорит о скорости в Memory Matrix.
 *
 * @param observedRtMs времена верных ответов в текущем заходе
 * @param storedPace темп по прошлой истории игрока
 */
export function sessionPace(
  observedRtMs: readonly number[],
  storedPace: number,
  referenceRtMs: number,
): number {
  const clampPace = (p: number) => Math.min(Math.max(p, MIN_PACE), MAX_PACE)

  // разминка: мягче собственного темпа игрока, а не фиксированные секунды -
  // быстрому не станет скучно, медленному не станет тесно
  if (observedRtMs.length < WARMUP_ROUNDS) {
    return clampPace(storedPace * WARMUP_FACTOR)
  }

  if (referenceRtMs <= 0) return clampPace(storedPace)

  const times = [...observedRtMs].filter((t) => t > 0).sort((a, b) => a - b)
  if (times.length === 0) return clampPace(storedPace)

  const mid = Math.floor(times.length / 2)
  const median =
    times.length % 2 === 1
      ? (times[mid] as number)
      : ((times[mid - 1] as number) + (times[mid] as number)) / 2

  const observed = median / referenceRtMs
  const n = times.length
  const mixed = (STORED_PACE_WEIGHT * storedPace + n * observed) / (STORED_PACE_WEIGHT + n)

  return clampPace(mixed)
}
